<template>
  <div class="cf-renderer">
    <!-- Skeleton loader -->
    <template v-if="loading">
      <div v-for="n in 2" :key="'CFRskel' + n" class="cf-renderer__skel-row">
        <span class="skel skel--line-sm" style="width: 30%;"></span>
        <span class="skel skel--text" style="height: 36px; margin-top: 6px;"></span>
      </div>
    </template>

    <div
      v-else
      v-for="f in fields"
      :key="'CFR' + f.id"
      class="cf-renderer__field"
    >
      <!-- READ MODE -->
      <template v-if="mode === 'read'">
        <div class="cf-renderer__read-label">{{ f.label }}</div>
        <div class="cf-renderer__read-value text-white">{{ readDisplay(f) }}</div>
      </template>

      <!-- WRITE MODE -->
      <template v-else>
        <!-- Checkbox (type 1) -->
        <div v-if="Number(f.type_id) === 1" class="cf-renderer__checkbox">
          <el-checkbox
            :value="isChecked(f.custom_field_id)"
            :disabled="disabled"
            @change="val => updateCheckbox(f.custom_field_id, val)"
          >
            <span class="text-white">
              {{ f.label }}
              <a
                v-for="(el, i) in (f.external_links || [])"
                :key="'EL' + f.id + '-' + i"
                :href="el.link"
                target="_blank"
                rel="noopener"
                class="cf-renderer__link"
                @click.stop
              >{{ el.link_text }}</a>
              <button
                v-for="(p, i) in (f.policies || [])"
                :key="'PL' + f.id + '-' + i"
                type="button"
                class="cf-renderer__link cf-renderer__link--btn"
                @click.stop="openPolicy(p)"
              >{{ p.policy_link_text }}</button>
              <span v-if="f.required" class="cf-renderer__required">*</span>
            </span>
          </el-checkbox>
          <div v-if="fieldErrors[f.custom_field_id]" class="cf-renderer__error">
            {{ fieldErrors[f.custom_field_id] }}
          </div>
        </div>

        <!-- Input field (type 2) -->
        <div v-else-if="Number(f.type_id) === 2">
          <label class="cf-renderer__label">
            {{ f.label }}
            <span v-if="f.required" class="cf-renderer__required">*</span>
          </label>
          <base-input
            class="mb-0"
            :error="fieldErrors[f.custom_field_id]"
            :value="getString(f.custom_field_id)"
            :disabled="disabled"
            :placeholder="f.placeholder || ''"
            @input="val => updateString(f.custom_field_id, val)"
          />
        </div>

        <!-- Dropdown (type 3) -->
        <div v-else-if="Number(f.type_id) === 3">
          <label class="cf-renderer__label">
            {{ f.label }}
            <span v-if="f.required" class="cf-renderer__required">*</span>
          </label>
          <el-select
            :value="responses[f.custom_field_id] || null"
            :disabled="disabled"
            class="select-primary cf-renderer__select"
            :class="{ 'cf-renderer__select--error': fieldErrors[f.custom_field_id] }"
            placeholder="Select an option"
            popper-class="cf-renderer__select-popper"
            :popper-append-to-body="false"
            value-key="id"
            @change="opt => updateDropdownOption(f, opt)"
          >
            <el-option
              v-for="opt in f.dropdown_options || []"
              :key="'CFRO' + opt.id"
              class="select-primary"
              :label="opt.name"
              :value="opt"
            />
          </el-select>
          <div v-if="fieldErrors[f.custom_field_id]" class="cf-renderer__error">
            {{ fieldErrors[f.custom_field_id] }}
          </div>

          <!-- Follow-up text field when selected option requires it -->
          <div v-if="shouldShowFollowUp(f)" class="cf-renderer__followup">
            <label class="cf-renderer__label">
              {{ followUpLabel(f) }}
            </label>
            <base-input
              class="mb-0"
              :error="fieldErrors[followUpKey(f.custom_field_id)]"
              :value="(responses[f.custom_field_id] && responses[f.custom_field_id].follow_up) || ''"
              :disabled="disabled"
              @input="val => updateFollowUp(f, val)"
            />
          </div>
        </div>
      </template>
    </div>

    <el-dialog
      :visible.sync="policyModalVisible"
      :title="selectedPolicy ? selectedPolicy.policy_link_text : ''"
      width="600px"
      custom-class="cf-renderer__policy-dialog"
      append-to-body
    >
      <div
        v-if="selectedPolicy"
        class="cf-renderer__policy-body"
        v-html="selectedPolicy.policy"
      ></div>
    </el-dialog>
  </div>
</template>

<script>
import { Checkbox, Select, Option, Dialog } from 'element-ui'
import { validateCustomFieldResponses } from '@/util/customFieldValidation'

export default {
  name: 'custom-field-response-renderer',
  components: {
    [Checkbox.name]: Checkbox,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
  },
  props: {
    fields: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Object,
      default: () => ({}),
    },
    mode: {
      type: String,
      default: 'write',
      validator: v => ['write', 'read'].includes(v),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fieldErrors: {},
      policyModalVisible: false,
      selectedPolicy: null,
    }
  },
  computed: {
    responses() {
      return this.value || {}
    },
    isValid() {
      if (this.mode !== 'write') return true
      return validateCustomFieldResponses(this.fields, this.responses)
    },
  },
  watch: {
    isValid: {
      immediate: true,
      handler(v) {
        this.$emit('validity', v)
      },
    },
    value: {
      immediate: true,
      handler() {
        this.normalizeLegacyDropdownResponses()
      },
    },
    fields: {
      immediate: true,
      handler() {
        this.normalizeLegacyDropdownResponses()
      },
    },
  },
  methods: {
    followUpKey(id) {
      return id + '_follow_up'
    },
    clearError(id) {
      if (this.fieldErrors[id]) {
        const next = { ...this.fieldErrors }
        delete next[id]
        this.fieldErrors = next
      }
    },
    validate() {
      const next = {}
      for (const f of this.fields || []) {
        if (!f.required) continue
        const raw = this.responses[f.custom_field_id]
        const typeId = Number(f.type_id)

        if (typeId === 1) {
          if (!raw) {
            next[f.custom_field_id] = 'This field is required'
          }
        } else if (typeId === 2) {
          if (raw === undefined || raw === null || !String(raw).trim()) {
            next[f.custom_field_id] = 'This field is required'
          }
        } else if (typeId === 3) {
          if (!raw || typeof raw !== 'object' || raw.id == null) {
            next[f.custom_field_id] = 'Please select an option'
          } else if (raw.has_follow_up_field && !String(raw.follow_up || '').trim()) {
            next[this.followUpKey(f.custom_field_id)] = 'This field is required'
          }
        }
      }
      this.fieldErrors = next
      return Object.keys(next).length === 0
    },
    clearErrors() {
      this.fieldErrors = {}
    },
    // Convert legacy string responses (name or JSON {option, follow_up}) to the
    // option object shape that the el-select now binds to directly. Runs once
    // per value/fields change so parents can keep hydrating from backend strings.
    normalizeLegacyDropdownResponses() {
      if (!this.value || !this.fields) return
      let next = null
      for (const f of this.fields) {
        if (Number(f.type_id) !== 3) continue
        const raw = this.value[f.custom_field_id]
        if (!raw || (typeof raw === 'object' && raw.id != null)) continue
        let name = ''
        let followUp = ''
        if (typeof raw === 'string' && raw.startsWith('{')) {
          try {
            const parsed = JSON.parse(raw)
            name = parsed.option || ''
            followUp = parsed.follow_up || ''
          } catch (e) {
            name = raw
          }
        } else if (typeof raw === 'string') {
          name = raw
        }
        if (!name) continue
        const opt = (f.dropdown_options || []).find(o => o.name === name)
        if (!opt) continue
        next = next || { ...this.value }
        next[f.custom_field_id] = followUp ? { ...opt, follow_up: followUp } : opt
      }
      if (next) this.$emit('input', next)
    },
    // ---- getters used by the template ----
    isChecked(id) {
      return this.responses[id] === '1'
    },
    getString(id) {
      return this.responses[id] || ''
    },
    shouldShowFollowUp(f) {
      const r = this.responses[f.custom_field_id]
      return !!(r && typeof r === 'object' && r.has_follow_up_field)
    },
    followUpLabel(f) {
      const r = this.responses[f.custom_field_id]
      return (r && r.follow_up_field_label) || 'Please specify'
    },
    // ---- setters emit input ----
    emitUpdate(id, value) {
      const next = { ...this.responses, [id]: value }
      this.$emit('input', next)
    },
    updateCheckbox(id, checked) {
      this.clearError(id)
      this.emitUpdate(id, checked ? '1' : '')
    },
    updateString(id, val) {
      this.clearError(id)
      this.emitUpdate(id, val || '')
    },
    updateDropdownOption(f, opt) {
      this.clearError(f.custom_field_id)
      this.clearError(this.followUpKey(f.custom_field_id))
      this.emitUpdate(f.custom_field_id, opt || null)
    },
    updateFollowUp(f, followUp) {
      this.clearError(this.followUpKey(f.custom_field_id))
      const current = this.responses[f.custom_field_id]
      if (!current || typeof current !== 'object') return
      this.emitUpdate(f.custom_field_id, { ...current, follow_up: followUp || '' })
    },
    openPolicy(p) {
      this.selectedPolicy = p
      this.policyModalVisible = true
    },
    // ---- read-mode display ----
    readDisplay(f) {
      const raw = this.responses[f.custom_field_id]
      if (f.type_id === 1) return raw === '1' ? 'Yes' : 'No'
      if (f.type_id === 2) return raw || '—'
      if (f.type_id === 3) {
        if (!raw) return '—'
        if (typeof raw === 'object') {
          if (!raw.name) return '—'
          return raw.follow_up ? `${raw.name} (${raw.follow_up})` : raw.name
        }
        return raw
      }
      return raw || '—'
    },
  },
}
</script>

<style scoped>
.cf-renderer {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.cf-renderer__field {
  display: block;
}

.cf-renderer__skel-row {
  display: block;
  margin-bottom: 14px;
}
.cf-renderer__skel-row .skel {
  display: block;
}

.cf-renderer__label {
  display: block;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 6px;
}

.cf-renderer__required {
  color: #e14eca;
  margin-left: 2px;
}

.cf-renderer__checkbox ::v-deep .el-checkbox {
  display: flex;
  align-items: flex-start;
  white-space: normal;
  margin-right: 0;
}
.cf-renderer__checkbox ::v-deep .el-checkbox__input {
  margin-top: 2px;
}
.cf-renderer__checkbox ::v-deep .el-checkbox__label {
  padding-left: 8px;
  white-space: normal;
  word-break: break-word;
  line-height: 1.4;
  flex: 1;
  min-width: 0;
}
.cf-renderer__checkbox ::v-deep .el-checkbox__label > span {
  white-space: normal;
}

.cf-renderer__link {
  margin-left: 6px;
  color: #e14eca;
  font-size: 0.875rem;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 300;
}
.cf-renderer__link:hover {
  opacity: 0.85;
  color: #e14eca;
}
.cf-renderer__link--btn {
  background: none;
  border: 0;
  padding: 0;
}

.cf-renderer__select {
  width: 100%;
  display: block;
}
.cf-renderer__select ::v-deep .el-select {
  width: 100%;
}
.cf-renderer__select--error ::v-deep .el-input__inner {
  border-color: #ec250d;
}
.cf-renderer__select--error ::v-deep .el-input__inner:focus {
  box-shadow: 0 0 0 2px rgba(236, 37, 13, 0.25);
}

.cf-renderer__error {
  margin-top: 4px;
  font-size: 11px;
  color: #ec250d;
}

.cf-renderer__followup {
  margin-top: 10px;
  padding-left: 12px;
  border-left: 2px solid rgba(225, 78, 202, 0.3);
}

/* Read mode */
.cf-renderer__read-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 2px;
}
.cf-renderer__read-value {
  font-size: 0.875rem;
}
</style>

<style>
.cf-renderer__select-popper .el-select-dropdown__item {
  height: auto;
  min-height: 34px;
  line-height: 1.4;
  padding: 8px 20px;
  white-space: normal;
  word-break: break-word;
}

.white-content .cf-renderer__label {
  color: rgba(0, 0, 0, 0.55);
}
.white-content .cf-renderer__read-label {
  color: rgba(0, 0, 0, 0.5);
}
.white-content .cf-renderer__required {
  color: #cc5fad;
}
.white-content .cf-renderer__followup {
  border-left-color: rgba(225, 78, 202, 0.25);
}

.el-dialog.cf-renderer__policy-dialog {
  max-width: calc(100vw - 32px);
  margin-top: 8vh !important;
}
.cf-renderer__policy-dialog .cf-renderer__policy-body {
  color: rgba(255, 255, 255, 0.85);
  font-size: 0.875rem;
  line-height: 1.55;
  max-height: 60vh;
  overflow-y: auto;
  overflow-wrap: anywhere;
  word-break: break-word;
  scrollbar-width: thin;
  scrollbar-color: rgba(125, 125, 125, 0.35) transparent;
}
.cf-renderer__policy-dialog .cf-renderer__policy-body::-webkit-scrollbar {
  width: 6px;
}
.cf-renderer__policy-dialog .cf-renderer__policy-body::-webkit-scrollbar-track {
  background: transparent;
}
.cf-renderer__policy-dialog .cf-renderer__policy-body::-webkit-scrollbar-thumb {
  background-color: rgba(125, 125, 125, 0.35);
  border-radius: 3px;
}
.cf-renderer__policy-dialog .cf-renderer__policy-body::-webkit-scrollbar-thumb:hover {
  background-color: rgba(125, 125, 125, 0.55);
}
@media (max-width: 575.98px) {
  .el-dialog.cf-renderer__policy-dialog {
    margin-top: 5vh !important;
  }
  .cf-renderer__policy-dialog .el-dialog__header {
    padding: 14px 16px 10px;
  }
  .cf-renderer__policy-dialog .el-dialog__body {
    padding: 16px;
  }
}
.white-content .cf-renderer__policy-dialog .cf-renderer__policy-body {
  color: rgba(0, 0, 0, 0.75);
}
</style>
